import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Tooltip,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Collapse,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  DirectionsRun as RunIcon,
  Favorite as HealthIcon,
  EmojiEvents as TrophyIcon,
  People as PeopleIcon,
  CompareArrows as CompareIcon,
  Timeline as TimelineIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  History as HistoryIcon,
  ShowChart as ShowChartIcon,
} from '@mui/icons-material';
import AthleteProgressionTab from './AthleteProgressionTab';
import TeamLeadersTab from './TeamLeadersTab';
import { useAuth } from '../../context/AuthContext';
import { teamService } from '../../services/teamService';
import { activityService } from '../../services/activityService';
import { userService } from '../../services/userService';
import { Team, DailyActivity, User } from '../../types';
import { getActivityMiles, getActivityMilesForDisplay, isWorkoutSplitMilesActivity } from '../../utils/activityUtils';
import { sortByFirstName } from '../../utils/sortByFirstName';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { startOfWeek, endOfWeek, subWeeks, subMonths, format, parseISO } from 'date-fns';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';

const DEBUG_TEAM_ANALYTICS = false;
const debugLog = (...args: unknown[]) => {
  if (DEBUG_TEAM_ANALYTICS) {
    console.log(...args);
  }
};

const TEAM_ANALYTICS_STORAGE_KEY = 'teamAnalytics_preferences';

interface TeamAnalyticsPreferences {
  selectedTeam: string;
  selectedRunner: string;
  timeframe: string;
  weekStartsOn: number;
  dateRangeStart: string;
  dateRangeEnd: string;
  analyticsTab: number;
  selectedComparisonAthletes: string[];
  progressionEvent?: string;
  progressionDateStart?: string;
  progressionDateEnd?: string;
  progressionAthleteUserId?: string | null;
  leadersEvent?: string;
  leadersMode?: 'alltime' | 'range';
  leadersDateStart?: string;
  leadersDateEnd?: string;
}

const loadTeamAnalyticsPreferences = (userId: string, teamIds: string[]): Partial<TeamAnalyticsPreferences> | null => {
  try {
    const raw = localStorage.getItem(`${TEAM_ANALYTICS_STORAGE_KEY}_${userId}`);
    if (!raw) return null;
    const saved = JSON.parse(raw) as TeamAnalyticsPreferences;
    const validTeam = saved.selectedTeam === 'all' || teamIds.includes(saved.selectedTeam);
    return {
      ...saved,
      selectedTeam: validTeam ? saved.selectedTeam : 'all',
    };
  } catch {
    return null;
  }
};

const saveTeamAnalyticsPreferences = (
  userId: string,
  prefs: Omit<
    TeamAnalyticsPreferences,
    'dateRangeStart' | 'dateRangeEnd' | 'progressionDateStart' | 'progressionDateEnd' | 'leadersDateStart' | 'leadersDateEnd'
  > & {
    dateRange: { start: Date; end: Date };
    progressionEvent?: string;
    progressionDateRange?: { start: Date; end: Date };
    progressionAthleteUserId?: string | null;
    leadersEvent?: string;
    leadersMode?: 'alltime' | 'range';
    leadersDateRange?: { start: Date; end: Date };
  }
) => {
  try {
    const toSave: TeamAnalyticsPreferences = {
      progressionAthleteUserId: prefs.progressionAthleteUserId ?? null,
      selectedTeam: prefs.selectedTeam,
      selectedRunner: prefs.selectedRunner,
      timeframe: prefs.timeframe,
      weekStartsOn: prefs.weekStartsOn,
      analyticsTab: prefs.analyticsTab,
      selectedComparisonAthletes: prefs.selectedComparisonAthletes,
      dateRangeStart: prefs.dateRange.start.toISOString(),
      dateRangeEnd: prefs.dateRange.end.toISOString(),
      progressionEvent: prefs.progressionEvent,
      progressionDateStart: prefs.progressionDateRange
        ? format(prefs.progressionDateRange.start, 'yyyy-MM-dd')
        : undefined,
      progressionDateEnd: prefs.progressionDateRange
        ? format(prefs.progressionDateRange.end, 'yyyy-MM-dd')
        : undefined,
      leadersEvent: prefs.leadersEvent,
      leadersMode: prefs.leadersMode,
      leadersDateStart: prefs.leadersDateRange
        ? format(prefs.leadersDateRange.start, 'yyyy-MM-dd')
        : undefined,
      leadersDateEnd: prefs.leadersDateRange
        ? format(prefs.leadersDateRange.end, 'yyyy-MM-dd')
        : undefined,
    };
    localStorage.setItem(`${TEAM_ANALYTICS_STORAGE_KEY}_${userId}`, JSON.stringify(toSave));
  } catch (e) {
    console.warn('Failed to save Team Analytics preferences:', e);
  }
};

// Activity type color mapping
const ACTIVITY_COLORS: Record<string, string> = {
  'Cross Train': '#FF00FF', // Magenta
  'Run Easy': '#39FF14', // Neon Green
  'Run Long': '#89CFF0', // Baby Blue
  'Easy Miles': '#39FF14', // Combined bucket: Run Easy + Run Long + Warm up + Cool down
  'Run Race Day': '#FFD700', // Gold
  'Tempo': '#FFD700', // Yellow
  'Threshold': '#FF8C00', // Orange
  'VO2 Max': '#FF4444', // Red
  'Hard': '#FF4444',
  'Strength': '#9C27B0',
  'Workout': '#FF8C00', // Orange
  'Warm up': '#5DADE2', // Light blue
  'Cool down': '#BB8FCE', // Light purple
  'Recovery': '#90EE90', // Light Green
  'Base': '#87CEEB', // Sky Blue
  'Rest Day': '#808080', // Gray
  'No activity': '#BDBDBD', // Light gray (health-only / no type chosen)
  'Sick': '#FFB6C1', // Light Pink
  'Injured': '#FF6347', // Tomato Red
  'Easy': '#39FF14', // Neon Green (alias for Run Easy)
  'Long': '#89CFF0', // Baby Blue (alias for Run Long)
};

interface AnalyticsData {
  totalMiles: number;
  totalTime: number;
  avgHealthScore: number;
  activeStreaks: number;
  teamPRs: number;
  activities: DailyActivity[];
  athletes: User[]; // Note: This includes both athletes and coaches for head coaches
}

// Helper function to fetch activities by date range using activityService
const getUserActivitiesByDateRange = async (userId: string, startDate: string, endDate: string): Promise<DailyActivity[]> => {
  try {
    debugLog(`Fetching activities for user ${userId} from ${startDate} to ${endDate}`);
    
    // Use activityService which has proper permissions setup
    // Fetch a large number of recent activities and filter by date range
    const allActivities = await activityService.getUserActivities(userId, 1000);
    
    // Filter by date range
    const filteredActivities = allActivities.filter(activity => {
      return activity.date >= startDate && activity.date <= endDate;
    });
    
    // Sort by date descending (most recent first)
    filteredActivities.sort((a, b) => b.date.localeCompare(a.date));
    
    debugLog(`Found ${filteredActivities.length} activities for user ${userId} in date range:`, filteredActivities.map(a => ({ id: a.id, date: a.date, miles: getActivityMiles(a) })));
    
    return filteredActivities;
  } catch (error) {
    console.error('Error fetching activities by date range:', error);
    return [];
  }
};

const TeamAnalyticsTab: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>('all');
  const [selectedRunner, setSelectedRunner] = useState<string>('all');
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>(() => {
    const now = new Date();
    return {
      start: startOfWeek(now, { weekStartsOn: 0 }),
      end: endOfWeek(now, { weekStartsOn: 0 }),
    };
  });
  const [timeframe, setTimeframe] = useState<string>('week');
  const [weekStartsOn, setWeekStartsOn] = useState<number>(0); // 0 = Sunday, 1 = Monday, etc.
  const [analyticsTab, setAnalyticsTab] = useState(0);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedRunners, setSelectedRunners] = useState<string[]>([]);
  const [selectedComparisonAthletes, setSelectedComparisonAthletes] = useState<string[]>([]);
  const [progressionEvent, setProgressionEvent] = useState('400m');
  const [progressionDateRange, setProgressionDateRange] = useState<{ start: Date; end: Date }>(() => ({
    start: subMonths(new Date(), 6),
    end: new Date(),
  }));
  const [progressionAthleteUserId, setProgressionAthleteUserId] = useState<string | null>(null);
  const [leadersEvent, setLeadersEvent] = useState('5K');
  const [leadersMode, setLeadersMode] = useState<'alltime' | 'range'>('alltime');
  const [leadersDateRange, setLeadersDateRange] = useState<{ start: Date; end: Date }>(() => ({
    start: subMonths(new Date(), 6),
    end: new Date(),
  }));
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    totalMiles: 0,
    totalTime: 0,
    avgHealthScore: 0,
    activeStreaks: 0,
    teamPRs: 0,
    activities: [],
    athletes: [],
  });
  const hasRestoredPreferences = useRef(false);
  const lastSavedWeekStartsOnRef = useRef<number | null>(null);

  useEffect(() => {
    if (user?.role === 'coach' || user?.role === 'headCoach') {
      loadTeamsAndData();
    }
  }, [user]);

  // Restore saved preferences when teams first load
  useEffect(() => {
    if (teams.length > 0 && user?.id && (user?.role === 'coach' || user?.role === 'headCoach') && !hasRestoredPreferences.current) {
      hasRestoredPreferences.current = true;
      const teamIds = teams.map((t) => t.id);
      const saved = loadTeamAnalyticsPreferences(user.id, teamIds);
      const remoteWeekStartsOn = user.weekStartsOn;
      const normalizedRemoteWeekStartsOn =
        remoteWeekStartsOn === 0 ||
        remoteWeekStartsOn === 1 ||
        remoteWeekStartsOn === 2 ||
        remoteWeekStartsOn === 3 ||
        remoteWeekStartsOn === 4 ||
        remoteWeekStartsOn === 5 ||
        remoteWeekStartsOn === 6
          ? remoteWeekStartsOn
          : 0;

      if (saved) {
        setSelectedTeam(saved.selectedTeam ?? 'all');
        setSelectedRunner(saved.selectedRunner ?? 'all');
        const restoredTimeframe = saved.timeframe ?? 'week';
        const restoredWeekStartsOn = saved.weekStartsOn ?? normalizedRemoteWeekStartsOn;
        setTimeframe(restoredTimeframe);
        setWeekStartsOn(restoredWeekStartsOn);
        setAnalyticsTab(saved.analyticsTab ?? 0);
        setSelectedComparisonAthletes(saved.selectedComparisonAthletes ?? []);
        if (restoredTimeframe === 'custom' && saved.dateRangeStart && saved.dateRangeEnd) {
          setDateRange({
            start: new Date(saved.dateRangeStart),
            end: new Date(saved.dateRangeEnd),
          });
        } else if (restoredTimeframe === 'week' || restoredTimeframe === 'lastweek' || restoredTimeframe === '2weeks') {
          const now = new Date();
          const weekStartOpt = { weekStartsOn: restoredWeekStartsOn as 0 | 1 | 2 | 3 | 4 | 5 | 6 };
          if (restoredTimeframe === 'week') {
            setDateRange({ start: startOfWeek(now, weekStartOpt), end: endOfWeek(now, weekStartOpt) });
          } else if (restoredTimeframe === 'lastweek') {
            const lastWeek = subWeeks(now, 1);
            setDateRange({ start: startOfWeek(lastWeek, weekStartOpt), end: endOfWeek(lastWeek, weekStartOpt) });
          } else {
            setDateRange({ start: subWeeks(startOfWeek(now, weekStartOpt), 1), end: endOfWeek(now, weekStartOpt) });
          }
        }
        if (saved.progressionEvent) setProgressionEvent(saved.progressionEvent);
        if (saved.progressionDateStart && saved.progressionDateEnd) {
          setProgressionDateRange({
            start: parseISO(saved.progressionDateStart.slice(0, 10)),
            end: parseISO(saved.progressionDateEnd.slice(0, 10)),
          });
        }
        if (saved.progressionAthleteUserId !== undefined && saved.progressionAthleteUserId !== null) {
          setProgressionAthleteUserId(saved.progressionAthleteUserId);
        }
        if (saved.leadersEvent) setLeadersEvent(saved.leadersEvent);
        if (saved.leadersMode === 'alltime' || saved.leadersMode === 'range') setLeadersMode(saved.leadersMode);
        if (saved.leadersDateStart && saved.leadersDateEnd) {
          setLeadersDateRange({
            start: parseISO(saved.leadersDateStart.slice(0, 10)),
            end: parseISO(saved.leadersDateEnd.slice(0, 10)),
          });
        }
      } else {
        setWeekStartsOn(normalizedRemoteWeekStartsOn);
        const now = new Date();
        const weekStartOpt = { weekStartsOn: normalizedRemoteWeekStartsOn as 0 | 1 | 2 | 3 | 4 | 5 | 6 };
        setDateRange({
          start: startOfWeek(now, weekStartOpt),
          end: endOfWeek(now, weekStartOpt),
        });
      }
    }
  }, [teams, user?.id, user?.role, user?.weekStartsOn]);

  // Save preferences when filter selections change
  useEffect(() => {
    if (user?.id && (user?.role === 'coach' || user?.role === 'headCoach') && hasRestoredPreferences.current) {
      saveTeamAnalyticsPreferences(user.id, {
        selectedTeam,
        selectedRunner,
        timeframe,
        weekStartsOn,
        dateRange,
        analyticsTab,
        selectedComparisonAthletes,
        progressionEvent,
        progressionDateRange,
        progressionAthleteUserId,
        leadersEvent,
        leadersMode,
        leadersDateRange,
      });
    }
  }, [
    selectedTeam,
    selectedRunner,
    timeframe,
    weekStartsOn,
    dateRange,
    analyticsTab,
    selectedComparisonAthletes,
    progressionEvent,
    progressionDateRange,
    progressionAthleteUserId,
    leadersEvent,
    leadersMode,
    leadersDateRange,
    user?.id,
    user?.role,
  ]);

  // Persist week start preference to user profile so it syncs across devices.
  useEffect(() => {
    if (!user?.id || !hasRestoredPreferences.current) return;
    if (weekStartsOn !== 0 && weekStartsOn !== 1 && weekStartsOn !== 2 && weekStartsOn !== 3 && weekStartsOn !== 4 && weekStartsOn !== 5 && weekStartsOn !== 6) {
      return;
    }
    if (lastSavedWeekStartsOnRef.current === weekStartsOn) return;
    lastSavedWeekStartsOnRef.current = weekStartsOn;
    userService.updateWeekStartsOn(user.id, weekStartsOn).catch((err) => {
      console.error('Failed to persist week start preference:', err);
      lastSavedWeekStartsOnRef.current = null;
    });
  }, [user?.id, weekStartsOn]);

  useEffect(() => {
    if (teams.length > 0) {
      loadAnalyticsData();
    }
  }, [selectedTeam, selectedRunner, dateRange, teams]);

  // Validate selectedRunner when athletes load (e.g. runner may have left the team)
  useEffect(() => {
    if (
      selectedRunner !== 'all' &&
      analyticsData.athletes.length > 0 &&
      !analyticsData.athletes.some((a) => a.id === selectedRunner)
    ) {
      setSelectedRunner('all');
    }
  }, [analyticsData.athletes, selectedRunner]);

  const loadTeamsAndData = async () => {
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      // First ensure coach/head coach is added as member of their own teams
      await teamService.ensureCoachMembership(user.id);
      
      // For coaches and head coaches, check both coach-owned teams and team memberships
      const [coachTeams, memberTeams] = await Promise.all([
        teamService.getTeamsByCoach(user.id),
        teamService.getTeamsByAthlete(user.id)
      ]);
      
      // Combine and deduplicate teams
      const allTeams = [...coachTeams];
      memberTeams.forEach(memberTeam => {
        if (!allTeams.find(team => team.id === memberTeam.id)) {
          allTeams.push(memberTeam);
        }
      });
      
      setTeams(allTeams);
      debugLog('TeamAnalyticsTab: Coach/Head Coach teams found:', coachTeams.length, 'member teams:', memberTeams.length, 'total:', allTeams.length);
      
      if (allTeams.length > 0) {
        await loadAnalyticsData();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalyticsData = async () => {
    if (!user || teams.length === 0) return;

    try {
      // Get all team members
      const teamsToAnalyze = selectedTeam === 'all' 
        ? teams 
        : teams.filter(t => t.id === selectedTeam);

      let allUsers: User[] = []; // Renamed from allAthletes to allUsers to include coaches
      let allActivities: DailyActivity[] = [];
      const isHeadCoach = user.role === 'headCoach';
      
      debugLog('=== Starting loadAnalyticsData ===');
      debugLog('User role:', user.role, 'Is Head Coach:', isHeadCoach);
      debugLog('Teams to analyze:', teamsToAnalyze.length);

      for (const team of teamsToAnalyze) {
        debugLog(`Processing team: ${team.name} (${team.id})`);
        
        // Use the same approach for both coaches and head coaches: get team members first
        // This is the approach that works for regular coaches
        const usersToInclude = new Set<string>();
        
        debugLog(`\n🔍 HEAD COACH MODE: ${isHeadCoach ? 'YES' : 'NO'}`);
        debugLog(`Current user: ${user.displayName} (${user.id}, role: ${user.role})`);
        debugLog(`Team headCoachId: ${team.headCoachId || 'NOT SET'}`);
        
        // Get team members (same approach that works for coaches)
        const members = await teamService.getTeamMembers(team.id);
        debugLog(`Found ${members.length} members in team ${team.name}`);
        debugLog(`Member details:`, members.map(m => ({ id: m.id, athleteId: m.athleteId, role: m.role })));
        
        // Get full user details for each member
        // For both coaches and head coaches: include ALL team members (athletes + coaches + head coaches)
        // Both roles can see everyone on the team
        for (const member of members) {
          try {
            const userData = await userService.getUserById(member.athleteId);
            if (userData) {
              // Include everyone - both coaches and head coaches see all team members
              usersToInclude.add(userData.id);
              if (!allUsers.find(u => u.id === userData.id)) {
                allUsers.push(userData);
                debugLog(`Added user to allUsers: ${userData.displayName} (${userData.id}, role: ${userData.role})`);
              }
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
          }
        }
        
        // For both coaches and head coaches: include themselves if not already included
        if (user.id && !usersToInclude.has(user.id)) {
          usersToInclude.add(user.id);
          if (!allUsers.find(u => u.id === user.id)) {
            allUsers.push(user);
            debugLog(`Added coach/headCoach to allUsers: ${user.displayName} (${user.id}, role: ${user.role})`);
          }
        }
        
        // Ensure head coach from team.headCoachId is included (they might not be in teamMembers)
        if (team.headCoachId && !usersToInclude.has(team.headCoachId)) {
          try {
            const headCoachData = await userService.getUserById(team.headCoachId);
            if (headCoachData) {
              usersToInclude.add(headCoachData.id);
              if (!allUsers.find(u => u.id === headCoachData.id)) {
                allUsers.push(headCoachData);
                debugLog(`Added head coach (from team.headCoachId) to allUsers: ${headCoachData.displayName} (${headCoachData.id})`);
              }
            }
          } catch (error) {
            console.error('Error fetching head coach data:', error);
          }
        }
        
        // Now fetch activities for the team (query by teamId, then filter by userId)
        // This approach works better with Firestore rules because rules can evaluate teamId during query
        const usersToFetch = Array.from(usersToInclude);
        debugLog(`=== FETCHING ACTIVITIES FOR TEAM ${team.name} ===`);
        debugLog(`Users to fetch activities for: ${usersToFetch.length}`);
        debugLog(`User IDs:`, usersToFetch);
        debugLog(`User details:`, usersToFetch.map(id => {
          const u = allUsers.find(usr => usr.id === id);
          return u ? { id: u.id, name: u.displayName, role: u.role } : { id, name: 'NOT FOUND' };
        }));
        
        // Fetch team activities once (queries by teamId, which works with Firestore rules)
        // Then filter by userId and date range on the client side
        let teamActivities: DailyActivity[] = [];
        try {
          debugLog(`\n📊 Fetching team activities for team: ${team.name} (${team.id})`);
          teamActivities = await activityService.getTeamActivities(team.id, 1000);
          debugLog(`   Found ${teamActivities.length} total activities for team ${team.name}`);
        } catch (error) {
          console.error(`   ❌ Error fetching team activities:`, error);
        }
        
        // Filter activities by userId and date range for each user
        for (const userId of usersToFetch) {
            const userData = allUsers.find(u => u.id === userId);
            if (!userData) {
              console.warn(`⚠️ User data not found for userId: ${userId} - SKIPPING`);
              continue;
            }
            
            try {
              debugLog(`\n📊 Processing activities for: ${userData.displayName} (${userData.role}, ID: ${userData.id})`);
              
              // Filter team activities by userId and date range
              const filteredActivities = teamActivities.filter(activity => {
                const matchesUser = activity.userId === userId;
                const activityDate = parseISO(activity.date);
                const inRange = activityDate >= dateRange.start && activityDate <= dateRange.end;
                return matchesUser && inRange;
              });
              
              debugLog(`   Found ${filteredActivities.length} activities for ${userData.displayName} in date range`);
              
              // Add filtered activities to allActivities
              allActivities = [...allActivities, ...filteredActivities];
              debugLog(`   ✅ Added ${filteredActivities.length} activities to allActivities (total now: ${allActivities.length})`);
              
              // Show activity details
              if (filteredActivities.length > 0) {
                debugLog(`   Activity details:`, filteredActivities.map(a => ({ 
                  date: a.date, 
                  miles: getActivityMiles(a), 
                  userId: a.userId 
                })));
              }
            } catch (error: any) {
              console.error(`❌ Error fetching activities for user ${userId}:`, error);
              console.error(`   Error details:`, {
                message: error?.message,
                code: error?.code,
                stack: error?.stack,
                userId,
                userDisplayName: userData?.displayName,
                userRole: userData?.role
              });
              
              // If it's a permission error, log more details
              if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
                console.error(`   🔴 PERMISSION DENIED for head coach reading activities for user ${userId}`);
                console.error(`   This suggests the Firestore rules are blocking the read`);
                console.error(`   Head coach ID: ${user?.id}, Target user ID: ${userId}`);
                console.error(`   Team ID: ${team.id}`);
              }
            }
        }
        debugLog(`\n=== TEAM ${team.name} SUMMARY ===`);
        debugLog(`Total activities collected: ${allActivities.length}`);
        debugLog(`Activities by user:`, allActivities.reduce((acc, a) => {
          acc[a.userId] = (acc[a.userId] || 0) + 1;
          return acc;
        }, {} as Record<string, number>));
        debugLog(`================================\n`);
      }

      // Remove duplicate users
      allUsers = allUsers.filter((user, index, self) => 
        index === self.findIndex(u => u.id === user.id)
      );
      allUsers = sortByFirstName(allUsers, u => u.displayName);

      // Remove duplicate activities
      allActivities = allActivities.filter((activity, index, self) => 
        index === self.findIndex(a => a.id === activity.id)
      );
      
      debugLog('\n=== FINAL DATA COLLECTION SUMMARY ===');
      debugLog(`Total unique users collected: ${allUsers.length}`);
      debugLog(`Users:`, allUsers.map(u => ({ id: u.id, name: u.displayName, role: u.role })));
      debugLog(`Total unique activities collected: ${allActivities.length}`);
      const activitiesByUser = allActivities.reduce((acc, a) => {
        if (!acc[a.userId]) {
          const user = allUsers.find(u => u.id === a.userId);
          acc[a.userId] = { count: 0, miles: 0, name: user?.displayName || 'Unknown' };
        }
        acc[a.userId].count += 1;
        acc[a.userId].miles += getActivityMiles(a);
        return acc;
      }, {} as Record<string, { count: number; miles: number; name: string }>);
      debugLog(`Activities by userId:`, activitiesByUser);
      debugLog(`\n⚠️ CRITICAL CHECK: If only head coach data shows, verify:`);
      debugLog(`  1. Are other users in allUsers? ${allUsers.length > 1 ? 'YES' : 'NO - THIS IS THE PROBLEM!'}`);
      debugLog(`  2. Are activities fetched for other users? ${Object.keys(activitiesByUser).length > 1 ? 'YES' : 'NO - THIS IS THE PROBLEM!'}`);
      debugLog(`  3. Is selectedRunner filter applied? ${selectedRunner === 'all' ? 'NO (showing all)' : `YES (filtered to: ${selectedRunner})`}`);
      debugLog(`=====================================\n`);

      // Apply runner filter AFTER fetching all data
      // Head coaches see everything by default, then filter by selectedRunner
      // Note: filteredUsers should include ALL users for the dropdown, even if they have no activities
      let filteredActivities = allActivities;
      let filteredUsers = allUsers; // Keep all users in dropdown regardless of activities
      
      if (selectedRunner !== 'all') {
        filteredActivities = allActivities.filter(a => a.userId === selectedRunner);
        // Don't filter filteredUsers - keep all users in dropdown so user can quickly switch between runners
      }

      debugLog('=== Analytics Debug Summary ===');
      debugLog('Selected Team:', selectedTeam);
      debugLog('Selected Runner:', selectedRunner);
      debugLog('Date Range:', { start: dateRange.start.toISOString(), end: dateRange.end.toISOString() });
      debugLog('Is Head Coach:', isHeadCoach);
      debugLog('Total Users Found:', allUsers.length);
      debugLog('Users:', allUsers.map(u => ({ id: u.id, name: u.displayName, role: u.role })));
      debugLog('Total Activities Found:', allActivities.length);
      debugLog('Activities by User:', activitiesByUser);
      debugLog('Filtered Users:', filteredUsers.length);
      debugLog('Filtered Activities:', filteredActivities.length);
      debugLog('================================');

      // Calculate analytics using FILTERED data (after applying runner filter)
      const totalMiles = filteredActivities.reduce((sum, a) => sum + getActivityMiles(a), 0);
      const totalTime = filteredActivities.reduce((sum, a) => {
        // Calculate time from miles and pace, or use cross training minutes
        const miles = getActivityMiles(a);
        const runTime = miles > 0 ? miles * 8 : 0; // Rough estimate: 8 min/mile
        const crossMinutes = a.crossTrainingMinutes || 0;
        return sum + runTime + crossMinutes;
      }, 0);

      const healthScores = filteredActivities
        .filter(a => a.sleepScore > 0 || a.hydrationLevel > 0 || a.nutritionLevel > 0)
        .map(a => (a.sleepScore + a.hydrationLevel + a.nutritionLevel) / 3);
      
      const avgHealthScore = healthScores.length > 0 
        ? healthScores.reduce((sum, score) => sum + score, 0) / healthScores.length 
        : 0;

      // Calculate active streaks (simplified - users with activities this period)
      const usersWithActivities = new Set(filteredActivities.map(a => a.userId));
      const activeStreaks = usersWithActivities.size;

      // Count activities with notes containing "PR" (as DailyActivity doesn't have isPR field)
      // You may want to add a PR tracking field later
      const teamPRs = 0; // Placeholder - implement PR tracking as needed

      setAnalyticsData({
        totalMiles,
        totalTime,
        avgHealthScore,
        activeStreaks,
        teamPRs,
        activities: filteredActivities, // Use filtered activities
        athletes: allUsers, // Always use allUsers for dropdown so all runners are always visible
      });

    } catch (err: any) {
      console.error('Error loading analytics:', err);
      setError('Failed to calculate analytics');
    }
  };

  const handleTimeframeChange = (newTimeframe: string, overrideWeekStartsOn?: number) => {
    setTimeframe(newTimeframe);
    const now = new Date();
    const weekStart = overrideWeekStartsOn ?? weekStartsOn;
    const weekStartOpt = { weekStartsOn: weekStart as 0 | 1 | 2 | 3 | 4 | 5 | 6 };
    
    switch (newTimeframe) {
      case 'week':
        setDateRange({
          start: startOfWeek(now, weekStartOpt),
          end: endOfWeek(now, weekStartOpt),
        });
        break;
      case 'lastweek': {
        const lastWeek = subWeeks(now, 1);
        setDateRange({
          start: startOfWeek(lastWeek, weekStartOpt),
          end: endOfWeek(lastWeek, weekStartOpt),
        });
        break;
      }
      case '2weeks':
        setDateRange({
          start: subWeeks(startOfWeek(now, weekStartOpt), 1),
          end: endOfWeek(now, weekStartOpt),
        });
        break;
      case 'month':
        setDateRange({
          start: subWeeks(now, 4),
          end: now,
        });
        break;
      case 'custom':
        // Keep current date range
        break;
    }
  };

  const handleWeekStartsOnChange = (newWeekStartsOn: number) => {
    setWeekStartsOn(newWeekStartsOn);
    if (timeframe === 'week' || timeframe === 'lastweek' || timeframe === '2weeks') {
      handleTimeframeChange(timeframe, newWeekStartsOn);
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  // Helper function to get full activity type label (do not default to 'Rest Day' for missing/None)
  const getActivityTypeLabel = (activity: DailyActivity): string => {
    if (activity.runType === 'Run' && activity.runSubType && activity.runSubType !== 'Workout') {
      return `Run ${activity.runSubType}`;
    }
    if (isWorkoutSplitMilesActivity(activity)) {
      const w = activity.workoutType;
      if (w === 'VO2 Max' || w === 'Hard workout' || w === 'Hard') return 'Hard';
      if (w) return w;
      return 'Workout';
    }
    if (activity.runType === 'Cross Training') {
      return 'Cross Train';
    }
    if (activity.runType === 'Strength') {
      return 'Strength';
    }
    if (activity.runType === 'Rest Day') {
      return 'Rest Day';
    }
    if (activity.runType === 'Sick') {
      return 'Sick';
    }
    if (activity.runType === 'Injured') {
      return 'Injured';
    }
    if (activity.runType && activity.runType !== 'None') {
      return activity.runType;
    }
    return 'No activity';
  };

  const getActivityColor = (activityTypeLabel: string) => {
    return ACTIVITY_COLORS[activityTypeLabel] || '#999999';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (teams.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Teams Available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create a team first to view analytics.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters & Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <FilterIcon color="primary" />
          <Typography variant="h6">Filters & Date Range</Typography>
        </Box>
        
        <Grid container spacing={2}>
          {/* Team Selector */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Team</InputLabel>
              <Select
                value={selectedTeam}
                label="Team"
                onChange={(e) => setSelectedTeam(e.target.value)}
              >
                <MenuItem value="all">All Teams</MenuItem>
                {teams.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Runner Selector */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Runner</InputLabel>
              <Select
                value={selectedRunner}
                label="Runner"
                onChange={(e) => setSelectedRunner(e.target.value)}
              >
                <MenuItem value="all">All Runners</MenuItem>
                {analyticsData.athletes.map((athlete) => (
                  <MenuItem key={athlete.id} value={athlete.id}>
                    {athlete.displayName} {user?.role === 'headCoach' && athlete.role !== 'athlete' ? `(${athlete.role})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Timeframe Quick Select */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Timeframe</InputLabel>
              <Select
                value={timeframe}
                label="Timeframe"
                onChange={(e) => handleTimeframeChange(e.target.value)}
              >
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="lastweek">Last Week</MenuItem>
                <MenuItem value="2weeks">Last 2 Weeks</MenuItem>
                <MenuItem value="month">Last Month</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Week starts on - for "This Week", "Last Week", and "Last 2 Weeks" */}
          {(timeframe === 'week' || timeframe === 'lastweek' || timeframe === '2weeks') && (
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Week starts on</InputLabel>
                <Select
                  value={weekStartsOn}
                  label="Week starts on"
                  onChange={(e) => handleWeekStartsOnChange(Number(e.target.value))}
                >
                  <MenuItem value={0}>Sunday</MenuItem>
                  <MenuItem value={1}>Monday</MenuItem>
                  <MenuItem value={2}>Tuesday</MenuItem>
                  <MenuItem value={3}>Wednesday</MenuItem>
                  <MenuItem value={4}>Thursday</MenuItem>
                  <MenuItem value={5}>Friday</MenuItem>
                  <MenuItem value={6}>Saturday</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Compare Mode Toggle */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant={compareMode ? 'contained' : 'outlined'}
              startIcon={<CompareIcon />}
              onClick={() => setCompareMode(!compareMode)}
              sx={{ height: 40 }}
            >
              Compare Mode
            </Button>
          </Grid>

          {/* Custom Date Range */}
          {timeframe === 'custom' && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Start Date"
                    value={dateRange.start}
                    onChange={(date) => date && setDateRange({ ...dateRange, start: date })}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="End Date"
                    value={dateRange.end}
                    onChange={(date) => date && setDateRange({ ...dateRange, end: date })}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </LocalizationProvider>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <RunIcon color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Total Miles
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {analyticsData.totalMiles.toFixed(1)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {format(dateRange.start, 'MMM d')} - {format(dateRange.end, 'MMM d')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <HealthIcon color="error" />
                <Typography variant="body2" color="text.secondary">
                  Avg Health Score
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {analyticsData.avgHealthScore.toFixed(1)}/10
              </Typography>
              <LinearProgress
                variant="determinate"
                value={analyticsData.avgHealthScore * 10}
                sx={{ mt: 1, height: 8, borderRadius: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUpIcon color="success" />
                <Typography variant="body2" color="text.secondary">
                  Active Streaks
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {analyticsData.activeStreaks}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.role === 'headCoach' ? 'Runners training this period' : 'Athletes training this period'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrophyIcon color="warning" />
                <Typography variant="body2" color="text.secondary">
                  Personal Records
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {analyticsData.teamPRs}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Athletes with PRs
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TimelineIcon color="info" />
                <Typography variant="body2" color="text.secondary">
                  Total Training Time
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {formatTime(analyticsData.totalTime)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Running + Cross Training
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PeopleIcon color="secondary" />
                <Typography variant="body2" color="text.secondary">
                  {user?.role === 'headCoach' ? 'Total Runners' : 'Total Athletes'}
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {analyticsData.athletes.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Across {selectedTeam === 'all' ? teams.length : 1} team(s)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Visualization Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={analyticsTab}
          onChange={(e, newValue) => setAnalyticsTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="SitRep" icon={<TrendingUpIcon />} iconPosition="start" />
          <Tab label="Training Log" icon={<CalendarIcon />} iconPosition="start" />
          <Tab label="Health Metrics" icon={<HealthIcon />} iconPosition="start" />
          <Tab label="Individual Stats" icon={<PeopleIcon />} iconPosition="start" />
          <Tab label="Comparison" icon={<CompareIcon />} iconPosition="start" />
          <Tab label="Athlete progression" icon={<ShowChartIcon />} iconPosition="start" />
          <Tab label="Leaders" icon={<TrophyIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {analyticsTab === 0 && (
        <SitRepView 
          activities={selectedComparisonAthletes.length > 0 
            ? analyticsData.activities.filter(a => selectedComparisonAthletes.includes(a.userId))
            : analyticsData.activities}
          athletes={selectedComparisonAthletes.length > 0
            ? analyticsData.athletes.filter(a => selectedComparisonAthletes.includes(a.id))
            : analyticsData.athletes}
          getActivityTypeLabel={getActivityTypeLabel}
          getActivityColor={getActivityColor}
          dateRange={dateRange}
          selectedComparisonAthletes={selectedComparisonAthletes}
        />
      )}

      {analyticsTab === 1 && (
        <TrainingLogView 
          activities={selectedComparisonAthletes.length > 0 
            ? analyticsData.activities.filter(a => selectedComparisonAthletes.includes(a.userId))
            : analyticsData.activities}
          athletes={selectedComparisonAthletes.length > 0
            ? analyticsData.athletes.filter(a => selectedComparisonAthletes.includes(a.id))
            : analyticsData.athletes}
          getActivityTypeLabel={getActivityTypeLabel}
          getActivityColor={getActivityColor}
          selectedComparisonAthletes={selectedComparisonAthletes}
        />
      )}

      {analyticsTab === 2 && (
        <HealthMetricsView 
          activities={selectedComparisonAthletes.length > 0 
            ? analyticsData.activities.filter(a => selectedComparisonAthletes.includes(a.userId))
            : analyticsData.activities}
          athletes={selectedComparisonAthletes.length > 0
            ? analyticsData.athletes.filter(a => selectedComparisonAthletes.includes(a.id))
            : analyticsData.athletes}
          selectedComparisonAthletes={selectedComparisonAthletes}
        />
      )}

      {analyticsTab === 3 && (
        <IndividualStatsView 
          activities={selectedComparisonAthletes.length > 0 
            ? analyticsData.activities.filter(a => selectedComparisonAthletes.includes(a.userId))
            : analyticsData.activities}
          athletes={selectedComparisonAthletes.length > 0
            ? analyticsData.athletes.filter(a => selectedComparisonAthletes.includes(a.id))
            : analyticsData.athletes}
          getActivityTypeLabel={getActivityTypeLabel}
          getActivityColor={getActivityColor}
          selectedComparisonAthletes={selectedComparisonAthletes}
        />
      )}

      {analyticsTab === 4 && (
        <ComparisonView 
          activities={analyticsData.activities}
          athletes={analyticsData.athletes}
          getActivityTypeLabel={getActivityTypeLabel}
          getActivityColor={getActivityColor}
          selectedComparisonAthletes={selectedComparisonAthletes}
          setSelectedComparisonAthletes={setSelectedComparisonAthletes}
        />
      )}

      {analyticsTab === 5 &&
        (selectedTeam === 'all' ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Choose a single team in the filters above to review athlete progression by event and date range.
          </Alert>
        ) : (
          <AthleteProgressionTab
            key={selectedTeam}
            teamId={selectedTeam}
            initialEvent={progressionEvent}
            initialRange={progressionDateRange}
            initialAthleteUserId={progressionAthleteUserId}
            onPrefsChange={(p) => {
              setProgressionEvent(p.progressionEvent);
              if (p.progressionDateStart && p.progressionDateEnd) {
                setProgressionDateRange({
                  start: parseISO(p.progressionDateStart.slice(0, 10)),
                  end: parseISO(p.progressionDateEnd.slice(0, 10)),
                });
              }
              setProgressionAthleteUserId(p.progressionAthleteUserId);
            }}
          />
        ))}

      {analyticsTab === 6 &&
        (selectedTeam === 'all' ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Choose a single team in the filters above to see all-time or seasonal leaders.
          </Alert>
        ) : (
          <TeamLeadersTab
            key={selectedTeam}
            teamId={selectedTeam}
            initialEvent={leadersEvent}
            initialMode={leadersMode}
            initialRange={leadersDateRange}
            onPrefsChange={(p) => {
              setLeadersEvent(p.leadersEvent);
              setLeadersMode(p.leadersMode);
              if (p.leadersDateStart && p.leadersDateEnd) {
                setLeadersDateRange({
                  start: parseISO(p.leadersDateStart.slice(0, 10)),
                  end: parseISO(p.leadersDateEnd.slice(0, 10)),
                });
              }
            }}
          />
        ))}
    </Box>
  );
};

// SitRep (Situation Report) View Component
const SitRepView: React.FC<{
  activities: DailyActivity[];
  athletes: User[];
  getActivityTypeLabel: (activity: DailyActivity) => string;
  getActivityColor: (activityTypeLabel: string) => string;
  dateRange: { start: Date; end: Date };
  selectedComparisonAthletes: string[];
}> = ({ activities, athletes, getActivityTypeLabel, getActivityColor, dateRange, selectedComparisonAthletes }) => {
  const [healthTrendsView, setHealthTrendsView] = useState<'daily' | 'weekly'>('daily');
  const [healthTrendsExpanded, setHealthTrendsExpanded] = useState(false);
  
  // Detect if we're in multi-athlete comparison mode
  const isComparingMultiple = selectedComparisonAthletes.length > 1;
  
  // Calculate number of weeks in date range
  const daysDiff = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
  const numWeeks = Math.ceil(daysDiff / 7);

  // Calculate total miles and average per week (getActivityMiles excludes Run + Race Day app-wide)
  const totalMiles = activities.reduce((sum, a) => sum + getActivityMiles(a), 0);
  const avgMilesPerWeek = numWeeks > 0 ? totalMiles / numWeeks : 0;

  // Calculate cross training minutes by type
  const crossTrainingByType = activities.reduce((acc, a) => {
    if (a.crossTrainingMinutes > 0 && a.crossTrainingType) {
      acc[a.crossTrainingType] = (acc[a.crossTrainingType] || 0) + a.crossTrainingMinutes;
    }
    return acc;
  }, {} as Record<string, number>);

  const totalCrossTrainingMinutes = Object.values(crossTrainingByType).reduce((sum, mins) => sum + mins, 0);
  const avgCrossTrainingPerWeek = numWeeks > 0 ? totalCrossTrainingMinutes / numWeeks : 0;

  const totalStrengthMinutes = activities.reduce((sum, a) => sum + (a.strengthMinutes || 0), 0);
  const avgStrengthPerWeek = numWeeks > 0 ? totalStrengthMinutes / numWeeks : 0;
  const supplementalMinutesTotal = totalCrossTrainingMinutes + totalStrengthMinutes;
  const crossTrainingSharePct =
    supplementalMinutesTotal > 0 ? (totalCrossTrainingMinutes / supplementalMinutesTotal) * 100 : 0;
  const strengthSharePct =
    supplementalMinutesTotal > 0 ? (totalStrengthMinutes / supplementalMinutesTotal) * 100 : 0;

  // Group activities by week for mileage analysis
  const weeklyMileage: { weekStart: Date; miles: number }[] = [];
  for (let i = 0; i < numWeeks; i++) {
    const weekStart = new Date(dateRange.start);
    weekStart.setDate(weekStart.getDate() + (i * 7));
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);

    const weekMiles = activities
      .filter(a => {
        const activityDate = parseISO(a.date);
        return activityDate >= weekStart && activityDate <= weekEnd;
      })
      .reduce((sum, a) => sum + getActivityMiles(a), 0);

    weeklyMileage.push({ weekStart, miles: weekMiles });
  }

  const highestMileageWeek = weeklyMileage.reduce((max, week) => week.miles > max.miles ? week : max, weeklyMileage[0] || { weekStart: new Date(), miles: 0 });
  const lowestMileageWeek = weeklyMileage.reduce((min, week) => week.miles < min.miles ? week : min, weeklyMileage[0] || { weekStart: new Date(), miles: 0 });

  // Calculate health scores over time (daily data points)
  const dailyHealthData: { date: string; sleep: number; hydration: number; nutrition: number }[] = [];
  
  // Get all activities with health data, sorted by date
  const healthActivities = activities
    .filter(a => a.sleepScore > 0 || a.hydrationLevel > 0 || a.nutritionLevel > 0)
    .sort((a, b) => a.date.localeCompare(b.date));

  // Group by date and average if multiple athletes on same day
  const healthByDate = healthActivities.reduce((acc, a) => {
    if (!acc[a.date]) {
      acc[a.date] = { sleep: [], hydration: [], nutrition: [] };
    }
    if (a.sleepScore > 0) acc[a.date].sleep.push(a.sleepScore);
    if (a.hydrationLevel > 0) acc[a.date].hydration.push(a.hydrationLevel);
    if (a.nutritionLevel > 0) acc[a.date].nutrition.push(a.nutritionLevel);
    return acc;
  }, {} as Record<string, { sleep: number[]; hydration: number[]; nutrition: number[] }>);

  // Convert to array with averages
  Object.entries(healthByDate).forEach(([date, scores]) => {
    dailyHealthData.push({
      date: format(parseISO(date), 'MMM d'),
      sleep: scores.sleep.length > 0 ? scores.sleep.reduce((sum, s) => sum + s, 0) / scores.sleep.length : 0,
      hydration: scores.hydration.length > 0 ? scores.hydration.reduce((sum, h) => sum + h, 0) / scores.hydration.length : 0,
      nutrition: scores.nutrition.length > 0 ? scores.nutrition.reduce((sum, n) => sum + n, 0) / scores.nutrition.length : 0,
    });
  });

  // Calculate weekly health averages
  const weeklyHealthData: { week: string; sleep: number; hydration: number; nutrition: number }[] = [];
  for (let i = 0; i < numWeeks; i++) {
    const weekStart = new Date(dateRange.start);
    weekStart.setDate(weekStart.getDate() + (i * 7));
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);

    const weekActivities = activities.filter(a => {
      const activityDate = parseISO(a.date);
      return activityDate >= weekStart && activityDate <= weekEnd && (a.sleepScore > 0 || a.hydrationLevel > 0 || a.nutritionLevel > 0);
    });

    if (weekActivities.length > 0) {
      weeklyHealthData.push({
        week: format(weekStart, 'MMM d'),
        sleep: weekActivities.reduce((sum, a) => sum + a.sleepScore, 0) / weekActivities.length,
        hydration: weekActivities.reduce((sum, a) => sum + a.hydrationLevel, 0) / weekActivities.length,
        nutrition: weekActivities.reduce((sum, a) => sum + a.nutritionLevel, 0) / weekActivities.length,
      });
    }
  }

  // Miles breakdown by type; Easy Miles = Run Easy + Run Long + Warm up + Cool down
  const milesBreakdown = activities.reduce((acc, a) => {
    if (isWorkoutSplitMilesActivity(a)) {
      const easyMiles = (a.warmupMiles ?? 0) + (a.cooldownMiles ?? 0);
      if (easyMiles > 0) {
        acc['Easy Miles'] = (acc['Easy Miles'] || 0) + easyMiles;
      }
      const w = a.workoutType;
      const workoutKey =
        w === 'VO2 Max' || w === 'Hard workout' || w === 'Hard' ? 'Hard' : w || 'Workout';
      acc[workoutKey] = (acc[workoutKey] || 0) + (a.workoutMiles ?? 0);
    } else if (a.runType === 'Run') {
      const runMiles = getActivityMiles(a);
      if (runMiles > 0) {
        if (a.runSubType === 'Easy' || a.runSubType === 'Long') {
          acc['Easy Miles'] = (acc['Easy Miles'] || 0) + runMiles;
        } else {
          const runLabel = a.runSubType ? `Run ${a.runSubType}` : 'Run';
          acc[runLabel] = (acc[runLabel] || 0) + runMiles;
        }
      }
    }
    return acc;
  }, {} as Record<string, number>);

  // Activity type distribution: only include activities where the athlete chose an explicit type (exclude 'None' / health-only)
  const activitiesWithExplicitType = activities.filter(a => a.runType && a.runType !== 'None');
  const activityTypeDistribution = activitiesWithExplicitType.reduce((acc, a) => {
    const label = getActivityTypeLabel(a);
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Calculate per-athlete stats for comparison mode
  const athleteStats = isComparingMultiple ? athletes.map(athlete => {
    const athleteActivities = activities.filter(a => a.userId === athlete.id);
    const athleteTotalMiles = athleteActivities.reduce((sum, a) => sum + getActivityMiles(a), 0);
    const athleteAvgMilesPerWeek = numWeeks > 0 ? athleteTotalMiles / numWeeks : 0;
    
    const athleteCrossTraining = athleteActivities.reduce((sum, a) => sum + (a.crossTrainingMinutes || 0), 0);
    const athleteAvgCTPerWeek = numWeeks > 0 ? athleteCrossTraining / numWeeks : 0;
    const athleteStrength = athleteActivities.reduce((sum, a) => sum + (a.strengthMinutes || 0), 0);
    const athleteAvgStrengthPerWeek = numWeeks > 0 ? athleteStrength / numWeeks : 0;
    
    // Calculate weekly mileage for this athlete
    const athleteWeeklyMileage: { weekStart: Date; miles: number }[] = [];
    for (let i = 0; i < numWeeks; i++) {
      const weekStart = new Date(dateRange.start);
      weekStart.setDate(weekStart.getDate() + (i * 7));
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const weekMiles = athleteActivities
        .filter(a => {
          const activityDate = parseISO(a.date);
          return activityDate >= weekStart && activityDate <= weekEnd;
        })
        .reduce((sum, a) => sum + getActivityMiles(a), 0);

      athleteWeeklyMileage.push({ weekStart, miles: weekMiles });
    }

    const highestWeek = athleteWeeklyMileage.reduce((max, week) => week.miles > max.miles ? week : max, athleteWeeklyMileage[0] || { weekStart: new Date(), miles: 0 });
    const lowestWeek = athleteWeeklyMileage.reduce((min, week) => week.miles < min.miles ? week : min, athleteWeeklyMileage[0] || { weekStart: new Date(), miles: 0 });

    // Calculate miles by run type for this athlete
    const athleteMilesByType = athleteActivities.reduce((acc, a) => {
      const label = getActivityTypeLabel(a);
      const miles = getActivityMiles(a);
      if (miles > 0) {
        acc[label] = (acc[label] || 0) + miles;
      }
      return acc;
    }, {} as Record<string, number>);

    // Calculate average health score
    const healthActivities = athleteActivities.filter(a => a.sleepScore > 0 || a.hydrationLevel > 0 || a.nutritionLevel > 0);
    const avgHealth = healthActivities.length > 0
      ? healthActivities.reduce((sum, a) => sum + (a.sleepScore + a.hydrationLevel + a.nutritionLevel) / 3, 0) / healthActivities.length
      : 0;

    // Calculate health data for this athlete (daily or weekly based on view)
    const athleteHealthData = healthTrendsView === 'daily'
      ? healthActivities
          .sort((a, b) => a.date.localeCompare(b.date))
          .map(a => ({
            date: format(parseISO(a.date), 'MMM d'),
            sleep: a.sleepScore,
            hydration: a.hydrationLevel,
            nutrition: a.nutritionLevel,
          }))
      : (() => {
          const weeklyData: { week: string; sleep: number; hydration: number; nutrition: number }[] = [];
          for (let i = 0; i < numWeeks; i++) {
            const weekStart = new Date(dateRange.start);
            weekStart.setDate(weekStart.getDate() + (i * 7));
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekEnd.getDate() + 6);

            const weekActivities = athleteActivities.filter(a => {
              const activityDate = parseISO(a.date);
              return activityDate >= weekStart && activityDate <= weekEnd && (a.sleepScore > 0 || a.hydrationLevel > 0 || a.nutritionLevel > 0);
            });

            if (weekActivities.length > 0) {
              weeklyData.push({
                week: format(weekStart, 'MMM d'),
                sleep: weekActivities.reduce((sum, a) => sum + a.sleepScore, 0) / weekActivities.length,
                hydration: weekActivities.reduce((sum, a) => sum + a.hydrationLevel, 0) / weekActivities.length,
                nutrition: weekActivities.reduce((sum, a) => sum + a.nutritionLevel, 0) / weekActivities.length,
              });
            }
          }
          return weeklyData;
        })();

    return {
      athlete,
      totalMiles: athleteTotalMiles,
      avgMilesPerWeek: athleteAvgMilesPerWeek,
      totalCrossTraining: athleteCrossTraining,
      avgCTPerWeek: athleteAvgCTPerWeek,
      totalStrength: athleteStrength,
      avgStrengthPerWeek: athleteAvgStrengthPerWeek,
      highestWeek,
      lowestWeek,
      milesByType: athleteMilesByType,
      avgHealth,
      activitiesCount: athleteActivities.length,
      healthData: athleteHealthData,
    };
  }) : [];

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon color="primary" />
          Situation Report (SitRep)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {numWeeks}-week summary from {format(dateRange.start, 'MMM d, yyyy')} to {format(dateRange.end, 'MMM d, yyyy')}
        </Typography>
        {selectedComparisonAthletes.length > 0 && (
          <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <CompareIcon sx={{ fontSize: 16 }} />
            Filtered to {selectedComparisonAthletes.length} selected athlete{selectedComparisonAthletes.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>

      {/* Summary Cards - Show comparison when multiple athletes selected */}
      {isComparingMultiple ? (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Athlete Comparison
          </Typography>
          <Grid container spacing={3}>
            {athleteStats.map(stat => (
              <Grid key={stat.athlete.id} size={{ xs: 12, md: 6, lg: 4 }}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
                        {stat.athlete.displayName?.charAt(0) || '?'}
                      </Avatar>
                      <Box>
                        <Typography variant="h6">{stat.athlete.displayName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {stat.activitiesCount} activities
                        </Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">Total Miles</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {stat.totalMiles.toFixed(1)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {stat.avgMilesPerWeek.toFixed(1)} mi/wk
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid size={6}>
                        <Box sx={{ p: 2, bgcolor: 'secondary.50', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">Cross training (min)</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                            {Math.round(stat.totalCrossTraining)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {Math.round(stat.avgCTPerWeek)} min/wk avg
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                            Strength (min)
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'secondary.dark' }}>
                            {Math.round(stat.totalStrength)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {Math.round(stat.avgStrengthPerWeek)} min/wk avg
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid size={6}>
                        <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">Highest Week</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {stat.highestWeek.miles.toFixed(1)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(stat.highestWeek.weekStart, 'MMM d')}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid size={6}>
                        <Box sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">Lowest Week</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                            {stat.lowestWeek.miles.toFixed(1)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(stat.lowestWeek.weekStart, 'MMM d')}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid size={12}>
                        <Box sx={{ p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">Avg Health Score</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                              {stat.avgHealth.toFixed(1)}/10
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={stat.avgHealth * 10}
                              sx={{ flex: 1, height: 8, borderRadius: 1 }}
                              color="info"
                            />
                          </Box>
                        </Box>
                      </Grid>

                      <Grid size={12}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                          Miles by Type
                        </Typography>
                        {Object.entries(stat.milesByType).slice(0, 3).map(([type, miles]) => (
                          <Box key={type} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getActivityColor(type) }} />
                              <Typography variant="caption">{type}</Typography>
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              {miles.toFixed(1)} mi
                            </Typography>
                          </Box>
                        ))}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ bgcolor: 'primary.50', borderLeft: 4, borderColor: 'primary.main' }}>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>Total Miles</Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>{totalMiles.toFixed(1)}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Avg: {avgMilesPerWeek.toFixed(1)} mi/week
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ bgcolor: 'secondary.50', borderLeft: 4, borderColor: 'secondary.main' }}>
              <CardContent>
                <Typography variant="h6" color="secondary" gutterBottom>Cross Training</Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>{Math.round(totalCrossTrainingMinutes)}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Avg: {Math.round(avgCrossTrainingPerWeek)} min/week
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ bgcolor: 'success.50', borderLeft: 4, borderColor: 'success.main' }}>
              <CardContent>
                <Typography variant="h6" color="success.main" gutterBottom>Highest Week</Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>{highestMileageWeek.miles.toFixed(1)}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {format(highestMileageWeek.weekStart, 'MMM d')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ bgcolor: 'warning.50', borderLeft: 4, borderColor: 'warning.main' }}>
              <CardContent>
                <Typography variant="h6" color="warning.main" gutterBottom>Lowest Week</Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>{lowestMileageWeek.miles.toFixed(1)}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {format(lowestMileageWeek.weekStart, 'MMM d')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Total Miles & Miles by Type */}
      {totalMiles > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Total Miles &amp; Miles by Type</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Run + Race Day mileage is excluded from volume everywhere; log race distance under hard workouts (Tempo / Threshold / Hard).
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
              {totalMiles.toFixed(1)} mi total
            </Typography>
            <Box sx={{ mt: 2 }}>
              {Object.entries(milesBreakdown)
                .filter(([, miles]) => miles > 0)
                .sort((a, b) => b[1] - a[1])
                .map(([type, miles]) => {
                  const percentage = totalMiles > 0 ? (miles / totalMiles) * 100 : 0;
                  return (
                    <Box key={type} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{type}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {miles.toFixed(1)} mi ({percentage.toFixed(0)}%)
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{
                          height: 8,
                          borderRadius: 1,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: getActivityColor(type),
                          },
                        }}
                      />
                    </Box>
                  );
                })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Strength vs Cross Training (supplemental minutes in selected range) — after Total Miles & Miles by Type */}
      {(totalCrossTrainingMinutes > 0 || totalStrengthMinutes > 0) && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Strength vs Cross Training</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Compare total minutes logged as Strength vs Cross Training in this period
              {selectedComparisonAthletes.length > 0 ? ' (filtered athletes)' : ''}.
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'secondary.light',
                    bgcolor: 'secondary.50',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Cross training
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                    {Math.round(totalCrossTrainingMinutes)} min
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg {Math.round(avgCrossTrainingPerWeek)} min/week ·{' '}
                    {supplementalMinutesTotal > 0 ? `${crossTrainingSharePct.toFixed(0)}%` : '—'} of supplemental time
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: 'rgba(156, 39, 176, 0.08)',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Strength
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: ACTIVITY_COLORS.Strength }}>
                    {Math.round(totalStrengthMinutes)} min
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg {Math.round(avgStrengthPerWeek)} min/week ·{' '}
                    {supplementalMinutesTotal > 0 ? `${strengthSharePct.toFixed(0)}%` : '—'} of supplemental time
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              Mix (Cross training vs Strength)
            </Typography>
            <Box
              sx={{
                display: 'flex',
                height: 14,
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'grey.200',
              }}
            >
              <Box
                sx={{
                  width: `${crossTrainingSharePct}%`,
                  minWidth: totalCrossTrainingMinutes > 0 ? 4 : 0,
                  bgcolor: 'secondary.main',
                  transition: 'width 0.3s ease',
                }}
                title={`Cross training ${Math.round(totalCrossTrainingMinutes)} min`}
              />
              <Box
                sx={{
                  width: `${strengthSharePct}%`,
                  minWidth: totalStrengthMinutes > 0 ? 4 : 0,
                  bgcolor: ACTIVITY_COLORS.Strength,
                  transition: 'width 0.3s ease',
                }}
                title={`Strength ${Math.round(totalStrengthMinutes)} min`}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Cross Training by Type — below Strength vs Cross Training */}
      {Object.keys(crossTrainingByType).length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Cross Training by Type</Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {Object.entries(crossTrainingByType).map(([type, minutes]) => (
                <Grid key={type} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">{type}</Typography>
                      <Typography variant="h6">{Math.round(minutes)} min</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Avg: {Math.round(minutes / numWeeks)} min/week
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Activity Type Distribution */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Activity Type Distribution</Typography>
          {isComparingMultiple ? (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {athleteStats.map(stat => {
                const athleteActivities = activities.filter(a => a.userId === stat.athlete.id);
                const athleteExplicit = athleteActivities.filter(a => a.runType && a.runType !== 'None');
                const athleteDistribution = athleteExplicit.reduce((acc, a) => {
                  const label = getActivityTypeLabel(a);
                  acc[label] = (acc[label] || 0) + 1;
                  return acc;
                }, {} as Record<string, number>);

                return (
                  <Grid key={stat.athlete.id} size={{ xs: 12, md: 6, lg: 4 }}>
                    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, height: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                          {stat.athlete.displayName?.charAt(0) || '?'}
                        </Avatar>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {stat.athlete.displayName}
                        </Typography>
                      </Box>
                      {Object.entries(athleteDistribution)
                        .sort((a, b) => b[1] - a[1])
                        .map(([type, count]) => {
                          const percentage = athleteExplicit.length > 0 ? (count / athleteExplicit.length) * 100 : 0;
                          return (
                            <Box key={type} sx={{ mb: 1.5 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption">{type}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {count} ({percentage.toFixed(0)}%)
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={percentage}
                                sx={{
                                  height: 6,
                                  borderRadius: 1,
                                  bgcolor: 'grey.200',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: getActivityColor(type),
                                  },
                                }}
                              />
                            </Box>
                          );
                        })}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Box sx={{ mt: 2 }}>
              {Object.entries(activityTypeDistribution)
                .sort((a, b) => b[1] - a[1])
                .map(([type, count]) => {
                  const percentage = activitiesWithExplicitType.length > 0 ? (count / activitiesWithExplicitType.length) * 100 : 0;
                  return (
                    <Box key={type} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{type}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {count} activities ({percentage.toFixed(0)}%)
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{
                          height: 8,
                          borderRadius: 1,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: getActivityColor(type),
                          },
                        }}
                      />
                    </Box>
                  );
                })}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Health Trends — collapsed by default; expand to see day-by-day / weekly charts */}
      {(dailyHealthData.length > 0 || weeklyHealthData.length > 0) && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" sx={{ mb: 0 }}>
                Health Trends
                {healthTrendsExpanded && (
                  <>
                    {' '}
                    {healthTrendsView === 'daily' ? '(Day-by-Day)' : '(Weekly Averages)'}
                  </>
                )}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                {healthTrendsExpanded && (
                  <>
                    <Button
                      size="small"
                      variant={healthTrendsView === 'daily' ? 'contained' : 'outlined'}
                      onClick={() => setHealthTrendsView('daily')}
                    >
                      Daily
                    </Button>
                    <Button
                      size="small"
                      variant={healthTrendsView === 'weekly' ? 'contained' : 'outlined'}
                      onClick={() => setHealthTrendsView('weekly')}
                    >
                      Weekly
                    </Button>
                  </>
                )}
                <Button
                  size="small"
                  variant={healthTrendsExpanded ? 'text' : 'outlined'}
                  onClick={() => setHealthTrendsExpanded((v) => !v)}
                >
                  {healthTrendsExpanded ? 'Hide' : 'Show'}
                </Button>
              </Box>
            </Box>
            <Collapse in={healthTrendsExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {healthTrendsView === 'daily'
                    ? 'Track daily health metrics over the selected period'
                    : 'View weekly average health metrics for a broader trend overview'}
                </Typography>
            {isComparingMultiple ? (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                {athleteStats.map(stat => (
                  <Grid key={stat.athlete.id} size={{ xs: 12, md: 6, lg: 4 }}>
                    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, height: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                          {stat.athlete.displayName?.charAt(0) || '?'}
                        </Avatar>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {stat.athlete.displayName}
                        </Typography>
                      </Box>
                      
                      {/* Sleep */}
                      <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                        Sleep Score
                      </Typography>
                      <Box sx={{ maxHeight: 200, overflowY: 'auto', pr: 1, mb: 2 }}>
                        {stat.healthData.map((data: any, index: number) => {
                          const label = 'date' in data ? data.date : data.week;
                          return (
                            <Box key={index} sx={{ mb: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption">{label}</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                  {data.sleep > 0 ? `${data.sleep.toFixed(1)}` : 'N/A'}
                                </Typography>
                              </Box>
                              {data.sleep > 0 && (
                                <LinearProgress
                                  variant="determinate"
                                  value={data.sleep * 10}
                                  sx={{ height: 4, borderRadius: 1 }}
                                  color="primary"
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Box>

                      {/* Hydration */}
                      <Typography variant="caption" color="info.main" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                        Hydration Level
                      </Typography>
                      <Box sx={{ maxHeight: 200, overflowY: 'auto', pr: 1, mb: 2 }}>
                        {stat.healthData.map((data: any, index: number) => {
                          const label = 'date' in data ? data.date : data.week;
                          return (
                            <Box key={index} sx={{ mb: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption">{label}</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                                  {data.hydration > 0 ? `${data.hydration.toFixed(1)}` : 'N/A'}
                                </Typography>
                              </Box>
                              {data.hydration > 0 && (
                                <LinearProgress
                                  variant="determinate"
                                  value={data.hydration * 10}
                                  sx={{ height: 4, borderRadius: 1 }}
                                  color="info"
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Box>

                      {/* Nutrition */}
                      <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                        Nutrition Level
                      </Typography>
                      <Box sx={{ maxHeight: 200, overflowY: 'auto', pr: 1 }}>
                        {stat.healthData.map((data: any, index: number) => {
                          const label = 'date' in data ? data.date : data.week;
                          return (
                            <Box key={index} sx={{ mb: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption">{label}</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                  {data.nutrition > 0 ? `${data.nutrition.toFixed(1)}` : 'N/A'}
                                </Typography>
                              </Box>
                              {data.nutrition > 0 && (
                                <LinearProgress
                                  variant="determinate"
                                  value={data.nutrition * 10}
                                  sx={{ height: 4, borderRadius: 1 }}
                                  color="success"
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                {/* Sleep Trend */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                    Sleep Score
                  </Typography>
                  <Box sx={{ maxHeight: 400, overflowY: 'auto', pr: 1 }}>
                    {(healthTrendsView === 'daily' ? dailyHealthData : weeklyHealthData).map((data, index) => {
                    const label = 'date' in data ? data.date : data.week;
                    const sleepScore = data.sleep;
                    return (
                      <Box key={index} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>{label}</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {sleepScore > 0 ? `${sleepScore.toFixed(1)}/10` : 'N/A'}
                          </Typography>
                        </Box>
                        {sleepScore > 0 && (
                          <LinearProgress
                            variant="determinate"
                            value={sleepScore * 10}
                            sx={{ height: 6, borderRadius: 1 }}
                            color="primary"
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Grid>

              {/* Hydration Trend */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="subtitle2" color="info.main" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'info.main' }} />
                  Hydration Level
                </Typography>
                <Box sx={{ maxHeight: 400, overflowY: 'auto', pr: 1 }}>
                  {(healthTrendsView === 'daily' ? dailyHealthData : weeklyHealthData).map((data, index) => {
                    const label = 'date' in data ? data.date : data.week;
                    const hydrationScore = data.hydration;
                    return (
                      <Box key={index} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>{label}</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                            {hydrationScore > 0 ? `${hydrationScore.toFixed(1)}/10` : 'N/A'}
                          </Typography>
                        </Box>
                        {hydrationScore > 0 && (
                          <LinearProgress
                            variant="determinate"
                            value={hydrationScore * 10}
                            sx={{ height: 6, borderRadius: 1 }}
                            color="info"
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Grid>

              {/* Nutrition Trend */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="subtitle2" color="success.main" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                  Nutrition Level
                </Typography>
                <Box sx={{ maxHeight: 400, overflowY: 'auto', pr: 1 }}>
                  {(healthTrendsView === 'daily' ? dailyHealthData : weeklyHealthData).map((data, index) => {
                    const label = 'date' in data ? data.date : data.week;
                    const nutritionScore = data.nutrition;
                    return (
                      <Box key={index} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>{label}</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {nutritionScore > 0 ? `${nutritionScore.toFixed(1)}/10` : 'N/A'}
                          </Typography>
                        </Box>
                        {nutritionScore > 0 && (
                          <LinearProgress
                            variant="determinate"
                            value={nutritionScore * 10}
                            sx={{ height: 6, borderRadius: 1 }}
                            color="success"
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Grid>
            </Grid>
            )}
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      )}
    </Paper>
  );
};

// Training Log View Component
const TrainingLogView: React.FC<{
  activities: DailyActivity[];
  athletes: User[];
  getActivityTypeLabel: (activity: DailyActivity) => string;
  getActivityColor: (activityTypeLabel: string) => string;
  selectedComparisonAthletes: string[];
}> = ({ activities, athletes, getActivityTypeLabel, getActivityColor, selectedComparisonAthletes }) => {
  const [sortBy, setSortBy] = useState<string>('date');

  // Omit health-only rows (no run type); avoids duplicate date/athlete lines when a real activity exists
  const trainingActivities = activities.filter((a) => getActivityTypeLabel(a) !== 'No activity');

  const sortedActivities = [...trainingActivities].sort((a, b) => {
    switch (sortBy) {
      case 'date':
        // DailyActivity.date is a string in YYYY-MM-DD format, so we can compare directly
        return b.date.localeCompare(a.date);
      case 'miles':
        return getActivityMilesForDisplay(b) - getActivityMilesForDisplay(a);
      case 'athlete':
        const athleteA = athletes.find(at => at.id === a.userId);
        const athleteB = athletes.find(at => at.id === b.userId);
        return (athleteA?.displayName || '').localeCompare(athleteB?.displayName || '');
      default:
        return 0;
    }
  });

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6">Training Activities</Typography>
          {selectedComparisonAthletes.length > 0 && (
            <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <CompareIcon sx={{ fontSize: 16 }} />
              Filtered to {selectedComparisonAthletes.length} selected athlete{selectedComparisonAthletes.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sort By</InputLabel>
          <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value)}>
            <MenuItem value="date">Date (Recent)</MenuItem>
            <MenuItem value="miles">Miles (High-Low)</MenuItem>
            <MenuItem value="athlete">Athlete (A-Z)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Activity Type Legend */}
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {Object.entries(ACTIVITY_COLORS)
          .filter(([type]) => type !== 'No activity')
          .map(([type, color]) => (
          <Chip
            key={type}
            label={type}
            size="small"
            sx={{
              backgroundColor: color,
              color: '#fff',
              fontWeight: 'bold',
            }}
          />
        ))}
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Athlete</TableCell>
              <TableCell>Activity Type</TableCell>
              <TableCell align="right">Miles</TableCell>
              <TableCell align="right">Time</TableCell>
              <TableCell align="right">Pace</TableCell>
              <TableCell>Health</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedActivities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No training activities found for this period
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedActivities.map((activity, index) => {
                const athlete = athletes.find(a => a.id === activity.userId);
                const activityTypeLabel = getActivityTypeLabel(activity);
                const activityColor = getActivityColor(activityTypeLabel);
                // Display miles (includes Race Day); volume totals use getActivityMiles elsewhere
                const activityMiles = getActivityMilesForDisplay(activity);
                const pace = activityMiles > 0
                  ? '8.0'
                  : '-';

                return (
                  <TableRow key={`${activity.id}-${index}`} hover>
                    <TableCell>{format(parseISO(activity.date), 'MMM d, yyyy')}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                          {athlete?.displayName?.charAt(0) || '?'}
                        </Avatar>
                        {athlete?.displayName || 'Unknown'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={activityTypeLabel}
                        size="small"
                        sx={{
                          backgroundColor: activityColor,
                          color: '#fff',
                          fontWeight: 'bold',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {activityMiles > 0 ? `${activityMiles.toFixed(2)} mi` : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {activity.crossTrainingMinutes > 0 && (
                        <Typography variant="body2" component="span" display="block">
                          {activity.crossTrainingMinutes} min (CT)
                        </Typography>
                      )}
                      {(activity.strengthMinutes ?? 0) > 0 && (
                        <Typography variant="body2" component="span" display="block">
                          {activity.strengthMinutes} min (strength)
                        </Typography>
                      )}
                      {activity.crossTrainingMinutes <= 0 &&
                        (activity.strengthMinutes ?? 0) <= 0 &&
                        (activityMiles > 0 ? `~${Math.round(activityMiles * 8)} min` : '-')}
                    </TableCell>
                    <TableCell align="right">
                      {pace !== '-' ? `~${pace} min/mi` : '-'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={`Sleep: ${activity.sleepScore}/10, Hydration: ${activity.hydrationLevel}/10, Nutrition: ${activity.nutritionLevel}/10`}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            Health: {((activity.sleepScore + activity.hydrationLevel + activity.nutritionLevel) / 3).toFixed(1)}/10
                          </Typography>
                        </Tooltip>
                        {activity.lastModifiedByRole === 'coach' && (
                          <Tooltip title={activity.coachNotes || 'Adjusted by coach'}>
                            <PersonIcon fontSize="small" color="warning" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// Health Metrics View Component
const HealthMetricsView: React.FC<{
  activities: DailyActivity[];
  athletes: User[];
  selectedComparisonAthletes: string[];
}> = ({ activities, athletes, selectedComparisonAthletes }) => {
  // Group activities by athlete
  const athleteHealthData = athletes.map(athlete => {
    const athleteActivities = activities.filter(a => a.userId === athlete.id);
    
    const healthActivities = athleteActivities.filter(
      a => a.sleepScore > 0 || a.hydrationLevel > 0 || a.nutritionLevel > 0
    );

    const avgSleep = healthActivities.length > 0
      ? healthActivities.reduce((sum, a) => sum + a.sleepScore, 0) / healthActivities.length
      : 0;

    const avgHydration = healthActivities.length > 0
      ? healthActivities.reduce((sum, a) => sum + a.hydrationLevel, 0) / healthActivities.length
      : 0;

    const avgNutrition = healthActivities.length > 0
      ? healthActivities.reduce((sum, a) => sum + a.nutritionLevel, 0) / healthActivities.length
      : 0;

    const overallHealth = (avgSleep + avgHydration + avgNutrition) / 3;

    // Count coach-modified entries
    const coachModifiedCount = athleteActivities.filter(
      a => a.lastModifiedByRole === 'coach'
    ).length;

    return {
      athlete,
      avgSleep,
      avgHydration,
      avgNutrition,
      overallHealth,
      dataPoints: healthActivities.length,
      coachModifiedCount,
    };
  });

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Health Metrics Overview
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: selectedComparisonAthletes.length > 0 ? 1 : 3 }}>
        Average health metrics scores (1-10 scale) based on daily check-ins
      </Typography>
      {selectedComparisonAthletes.length > 0 && (
        <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 3 }}>
          <CompareIcon sx={{ fontSize: 16 }} />
          Filtered to {selectedComparisonAthletes.length} selected athlete{selectedComparisonAthletes.length !== 1 ? 's' : ''}
        </Typography>
      )}

      <Grid container spacing={3}>
        {athleteHealthData.map(data => (
          <Grid key={data.athlete.id} size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar>
                    {data.athlete.displayName?.charAt(0) || '?'}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">{data.athlete.displayName}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {data.dataPoints} check-ins
                      </Typography>
                      {data.coachModifiedCount > 0 && (
                        <Tooltip title={`${data.coachModifiedCount} entries adjusted by coach`}>
                          <Chip
                            icon={<PersonIcon fontSize="small" />}
                            label={data.coachModifiedCount}
                            size="small"
                            color="warning"
                            sx={{ height: 18, fontSize: '0.7rem' }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  <Chip
                    label={`${data.overallHealth.toFixed(1)}/10`}
                    color={data.overallHealth >= 7 ? 'success' : data.overallHealth >= 5 ? 'warning' : 'error'}
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Sleep Quality</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {data.avgSleep.toFixed(1)}/10
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={data.avgSleep * 10}
                    sx={{ height: 8, borderRadius: 1 }}
                    color={data.avgSleep >= 7 ? 'success' : 'warning'}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Hydration</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {data.avgHydration.toFixed(1)}/10
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={data.avgHydration * 10}
                    sx={{ height: 8, borderRadius: 1 }}
                    color={data.avgHydration >= 7 ? 'success' : 'warning'}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Nutrition</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {data.avgNutrition.toFixed(1)}/10
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={data.avgNutrition * 10}
                    sx={{ height: 8, borderRadius: 1 }}
                    color={data.avgNutrition >= 7 ? 'success' : 'warning'}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {athleteHealthData.length === 0 && (
          <Grid size={{ xs: 12 }}>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No health metrics data available for this period
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

// Individual Stats View Component
const IndividualStatsView: React.FC<{
  activities: DailyActivity[];
  athletes: User[];
  getActivityTypeLabel: (activity: DailyActivity) => string;
  getActivityColor: (activityTypeLabel: string) => string;
  selectedComparisonAthletes: string[];
}> = ({ activities, athletes, getActivityTypeLabel, getActivityColor, selectedComparisonAthletes }) => {
  const athleteStats = athletes.map(athlete => {
    const athleteActivities = activities.filter(a => a.userId === athlete.id);
    
    const totalMiles = athleteActivities.reduce((sum, a) => sum + getActivityMiles(a), 0);
    const totalTime = athleteActivities.reduce((sum, a) => {
      const miles = getActivityMiles(a);
      const runTime = miles * 8; // Estimate 8 min/mile
      return sum + runTime + (a.crossTrainingMinutes || 0);
    }, 0);
    const activitiesCount = athleteActivities.length;
    const avgPace = totalMiles > 0 && totalTime > 0 ? totalTime / totalMiles : 0;
    const prs = 0; // PRs not tracked in DailyActivity yet

    // Activity type breakdown
    const activityTypes = athleteActivities.reduce((acc, a) => {
      const type = getActivityTypeLabel(a);
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      athlete,
      totalMiles,
      totalTime,
      activitiesCount,
      avgPace,
      prs,
      activityTypes,
    };
  }).sort((a, b) => b.totalMiles - a.totalMiles);

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Individual Athlete Statistics
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: selectedComparisonAthletes.length > 0 ? 1 : 3 }}>
        Sorted by total mileage (highest to lowest)
      </Typography>
      {selectedComparisonAthletes.length > 0 && (
        <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 3 }}>
          <CompareIcon sx={{ fontSize: 16 }} />
          Filtered to {selectedComparisonAthletes.length} selected athlete{selectedComparisonAthletes.length !== 1 ? 's' : ''}
        </Typography>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Athlete</TableCell>
              <TableCell align="right">Total Miles</TableCell>
              <TableCell align="right">Training Time</TableCell>
              <TableCell align="right">Activities</TableCell>
              <TableCell align="right">Avg Pace</TableCell>
              <TableCell align="right">PRs</TableCell>
              <TableCell>Activity Breakdown</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {athleteStats.map(stat => (
              <TableRow key={stat.athlete.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {stat.athlete.displayName?.charAt(0) || '?'}
                    </Avatar>
                    {stat.athlete.displayName}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    {stat.totalMiles.toFixed(1)} mi
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatTime(stat.totalTime)}</TableCell>
                <TableCell align="right">{stat.activitiesCount}</TableCell>
                <TableCell align="right">
                  {stat.avgPace > 0 ? `${stat.avgPace.toFixed(2)} min/mi` : '-'}
                </TableCell>
                <TableCell align="right">
                  {stat.prs > 0 ? (
                    <Chip
                      icon={<TrophyIcon />}
                      label={stat.prs}
                      size="small"
                      color="warning"
                    />
                  ) : '-'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {Object.entries(stat.activityTypes).map(([type, count]) => (
                      <Chip
                        key={type}
                        label={`${type}: ${count}`}
                        size="small"
                        sx={{
                          backgroundColor: getActivityColor(type),
                          color: '#fff',
                          fontSize: '0.7rem',
                        }}
                      />
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {athleteStats.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No athlete data available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// Comparison View Component
const ComparisonView: React.FC<{
  activities: DailyActivity[];
  athletes: User[];
  getActivityTypeLabel: (activity: DailyActivity) => string;
  getActivityColor: (activityTypeLabel: string) => string;
  selectedComparisonAthletes: string[];
  setSelectedComparisonAthletes: (athletes: string[]) => void;
}> = ({ activities, athletes, getActivityTypeLabel, getActivityColor, selectedComparisonAthletes, setSelectedComparisonAthletes }) => {

  const comparisonData = selectedComparisonAthletes.map(athleteId => {
    const athlete = athletes.find(a => a.id === athleteId);
    if (!athlete) return null;

    const athleteActivities = activities.filter(a => a.userId === athleteId);
    const totalMiles = athleteActivities.reduce((sum, a) => sum + getActivityMiles(a), 0);
    const totalTime = athleteActivities.reduce((sum, a) => {
      const miles = getActivityMiles(a);
      const runTime = miles * 8; // Estimate 8 min/mile
      return sum + runTime + (a.crossTrainingMinutes || 0);
    }, 0);
    const avgPace = totalMiles > 0 && totalTime > 0 ? totalTime / totalMiles : 0;
    
    const healthActivities = athleteActivities.filter(
      a => a.sleepScore > 0 || a.hydrationLevel > 0 || a.nutritionLevel > 0
    );
    const avgHealth = healthActivities.length > 0
      ? healthActivities.reduce((sum, a) => sum + (a.sleepScore + a.hydrationLevel + a.nutritionLevel) / 3, 0) / healthActivities.length
      : 0;

    return {
      athlete,
      totalMiles,
      totalTime,
      avgPace,
      avgHealth,
      activitiesCount: athleteActivities.length,
    };
  }).filter(Boolean);

  const handleSelectAll = () => {
    setSelectedComparisonAthletes(athletes.map(a => a.id));
  };

  const handleClearAll = () => {
    setSelectedComparisonAthletes([]);
  };

  const handleRemoveAthlete = (athleteId: string) => {
    setSelectedComparisonAthletes(selectedComparisonAthletes.filter(id => id !== athleteId));
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Compare Athletes
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select up to 10 athletes (or more) to compare their performance metrics side-by-side
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Select Athletes to Compare</InputLabel>
          <Select
            multiple
            value={selectedComparisonAthletes}
            label="Select Athletes to Compare"
            onChange={(e) => setSelectedComparisonAthletes(e.target.value as string[])}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const athlete = athletes.find(a => a.id === value);
                  return (
                    <Chip
                      key={value}
                      label={athlete?.displayName || 'Unknown'}
                      size="small"
                      onDelete={() => handleRemoveAthlete(value)}
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  );
                })}
              </Box>
            )}
          >
            {athletes.map((athlete) => (
              <MenuItem key={athlete.id} value={athlete.id}>
                {athlete.displayName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 120 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleSelectAll}
            disabled={selectedComparisonAthletes.length === athletes.length}
            fullWidth
          >
            Select All
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearAll}
            disabled={selectedComparisonAthletes.length === 0}
            fullWidth
          >
            Clear All
          </Button>
        </Box>
      </Box>

      {comparisonData.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CompareIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Select Athletes to Compare
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose athletes from the dropdown above or click "Select All" to compare the entire team
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {comparisonData.map(data => data && (
            <Grid key={data.athlete.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card>
                <CardContent>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        mx: 'auto',
                        mb: 1,
                        fontSize: '1.5rem',
                      }}
                    >
                      {data.athlete.displayName?.charAt(0) || '?'}
                    </Avatar>
                    <Typography variant="h6">{data.athlete.displayName}</Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Total Miles
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {data.totalMiles.toFixed(1)}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Activities
                    </Typography>
                    <Typography variant="h6">
                      {data.activitiesCount}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Avg Pace
                    </Typography>
                    <Typography variant="h6">
                      {data.avgPace > 0 ? `${data.avgPace.toFixed(2)} min/mi` : '-'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Health Score
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6">
                        {data.avgHealth.toFixed(1)}/10
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={data.avgHealth * 10}
                        sx={{ flex: 1, height: 8, borderRadius: 1 }}
                        color={data.avgHealth >= 7 ? 'success' : 'warning'}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  );
};

export default TeamAnalyticsTab;


